import { useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Upload, FileText, Image, Mail, Link2, QrCode, Info, ArrowRight, Users,
  CheckCircle2, AlertTriangle, Eye, Lock, Sparkles, ShieldCheck, Zap,
  X, ThumbsUp, ThumbsDown, MessageSquare, ArrowUpRight
} from "lucide-react";
import { useDemoState } from "@/data/demoState";

const sourceIcons = { "Outlook attachment": Mail, "QR site upload": QrCode, "Secure upload link": Link2, "Secure consultant link": Link2, "Manual upload": Upload, "Email attachment": Mail, "Drag and drop": Upload, "QR upload": QrCode };

const statusStyles = {
  "needs-review": { bg: "bg-amber-50", text: "text-amber-700", label: "Needs review", border: "border-amber-200" },
  "approved": { bg: "bg-emerald-50", text: "text-emerald-700", label: "Approved", border: "border-emerald-200" },
  "rejected": { bg: "bg-red-50", text: "text-red-700", label: "Rejected", border: "border-red-200" },
  "flagged": { bg: "bg-red-50", text: "text-red-700", label: "Flagged", border: "border-red-200" },
  "pending-match": { bg: "bg-blue-50", text: "text-blue-700", label: "Pending match", border: "border-blue-200" },
};

const aiEnrichment = {
  'image-low-conf': { detected: "Unable to identify specific item", confidence: 34, issues: ["Image is too blurry", "Low light conditions", "Subject not clearly visible"], recommendation: "Retake in daylight with steady hand." },
  'image-partial': { detected: "Footing reinforcement visible", confidence: 87, issues: ["Angle too close", "Edge partially cropped"], recommendation: "Wide-angle photo needed for complete coverage." },
};

function ConfidenceBar({ value }) {
  const color = value >= 90 ? 'bg-emerald-500' : value >= 70 ? 'bg-amber-500' : 'bg-red-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${value}%` }} />
      </div>
      <span className={`text-[10px] font-bold ${value >= 90 ? 'text-emerald-600' : value >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{value}%</span>
    </div>
  );
}

export default function ProofReview() {
  const { evidence, conditions, workspace, analytics, activeLens, approveEvidence, logAction, workProofs, approveWorkProof } = useDemoState();
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");

  // Merge work proofs into evidence list
  const workProofAsEvidence = workProofs.map(wp => ({
    id: wp.id,
    conditionId: null,
    conditionTitle: null,
    conditionNumber: null,
    project: wp.trade + ' — ' + (wp.company || ''),
    projectId: wp.projectId,
    fileName: wp.fileName,
    fileType: wp.fileType || 'image',
    uploadedBy: wp.uploadedBy,
    uploadedById: null,
    uploadMethod: 'SiteReady Trade Pass',
    uploadDate: wp.uploadDate,
    status: wp.status === 'approved' ? 'approved' : wp.status === 'rejected' ? 'flagged' : 'needs-review',
    matchConfidence: 92,
    fileSize: '3.2 MB',
    reviewer: wp.reviewedBy || 'Pending',
    notes: wp.notes || '',
    _isWorkProof: true,
    _wpId: wp.id,
  }));

  const allEvidence = [...evidence, ...workProofAsEvidence];

  const proofItems = allEvidence.map(ev => {
    const cond = ev.conditionId ? conditions.find(c => c.id === ev.conditionId) : null;
    const isImage = ev.fileType === 'image' || ev.fileName?.match(/\.(jpg|jpeg|png|webp)$/i);
    let aiAssist = null;
    if (isImage && ev.matchConfidence < 50) aiAssist = aiEnrichment['image-low-conf'];
    else if (isImage && ev.matchConfidence < 85 && ev.status === 'needs-review') aiAssist = aiEnrichment['image-partial'];
    if (ev.status === 'flagged') aiAssist = { detected: "Quality issue detected", confidence: ev.matchConfidence, issues: (ev.notes || '').split('. ').filter(Boolean), recommendation: ev.notes || 'Review required.' };
    return {
      ...ev,
      condition: cond ? `${cond.number} ${cond.title}` : null,
      condObj: cond,
      isBlocker: cond?.isBlocker || false,
      aiAssist,
      stage: cond?.stage || 'Unknown',
    };
  });

  const filtered = filter === "all" ? proofItems : proofItems.filter(p => p.status === filter);

  const handleApprove = (evId) => {
    const item = allEvidence.find(e => e.id === evId);
    if (item?._isWorkProof) {
      approveWorkProof(item._wpId);
      toast.success('Work proof approved', { description: 'Trade work proof verified and recorded.' });
    } else {
      approveEvidence(evId);
      toast.success('Evidence approved', { description: 'Condition cleared. Blocker removed. Stage readiness and trade status updated.' });
    }
    setSelected(null);
  };

  const handleReject = (evId) => {
    logAction(`Evidence rejected: ${evidence.find(e => e.id === evId)?.fileName}`, 'reject');
    toast.error('Evidence rejected', { description: 'Owner will be notified to re-upload.' });
    setSelected(null);
  };

  const sel = selected;
  const SourceIcon = sel ? (sourceIcons[sel.uploadMethod] || Upload) : Upload;

  return (
    <AppLayout>
      <div className="space-y-5" data-testid="proof-review-page">
        {/* Explainer cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { icon: Info, bg: "bg-blue-50/30", border: "border-blue-100", color: "text-blue-700", title: "What this page does", desc: "Review uploaded evidence, match to conditions, and approve to update stage readiness." },
            { icon: ArrowRight, bg: "bg-slate-50/30", border: "border-slate-200", color: "text-slate-600", title: "What happens next", desc: "Approved proof clears the linked condition, removes blocker, updates trade release, and may clear the stage." },
            { icon: Sparkles, bg: "bg-indigo-50/30", border: "border-indigo-100", color: "text-indigo-700", title: "AI Proof Assist", desc: "AI analyses images for quality, coverage, and condition match. Flagged items need manual review." },
          ].map((c, i) => (
            <Card key={i} className={`${c.border} ${c.bg}`}>
              <CardContent className="py-3 px-4 flex items-start gap-2.5">
                <c.icon className={`w-4 h-4 ${c.color} mt-0.5 shrink-0`} />
                <div><p className={`text-xs font-semibold ${c.color} mb-0.5`}>{c.title}</p><p className="text-xs text-slate-500">{c.desc}</p></div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Needs review", value: proofItems.filter(p => p.status === "needs-review" || p.status === "pending-match").length, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-100" },
            { label: "Approved", value: proofItems.filter(p => p.status === "approved").length, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-100" },
            { label: "Flagged", value: proofItems.filter(p => p.status === "flagged" || p.status === "rejected").length, color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
            { label: "Blocking stages", value: proofItems.filter(p => p.isBlocker && p.status !== "approved").length, color: "text-red-600", bg: "bg-red-50", border: "border-red-100" },
          ].map(s => (
            <Card key={s.label} className={`${s.bg} ${s.border} border`}>
              <CardContent className="py-3 px-4"><p className="text-xs font-medium text-slate-500">{s.label}</p><p className={`text-2xl font-heading font-bold ${s.color}`}>{s.value}</p></CardContent>
            </Card>
          ))}
        </div>

        {/* Analytics strip */}
        {analytics && analytics.proofIssues.length > 0 && activeLens !== 'client' && activeLens !== 'buyer' && (
          <div className="flex items-center gap-3 flex-wrap" data-testid="proof-analytics">
            <div className="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-lg px-3 py-1.5">
              <AlertTriangle className="w-3 h-3 text-red-500" />
              <span className="text-[10px] font-semibold text-red-700">{analytics.proofIssues.length} proof quality issue(s)</span>
            </div>
            {analytics.proofIssues.slice(0, 2).map((p, i) => (
              <div key={i} className="flex items-center gap-1.5 bg-amber-50 border border-amber-100 rounded-lg px-3 py-1.5">
                <Eye className="w-3 h-3 text-amber-500" />
                <span className="text-[10px] font-semibold text-amber-700">{p.file}: {p.reason}</span>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { id: "all", label: "All" },
            { id: "needs-review", label: "Needs review" },
            { id: "approved", label: "Approved" },
            { id: "flagged", label: "Flagged" },
            { id: "pending-match", label: "Pending match" },
          ].map(f => (
            <Button key={f.id} variant={filter === f.id ? "default" : "outline"} size="sm" className="text-xs" onClick={() => setFilter(f.id)} data-testid={`filter-${f.id}`}>
              {f.label} {f.id !== "all" && `(${proofItems.filter(p => f.id === 'flagged' ? (p.status === 'flagged' || p.status === 'rejected') : p.status === f.id).length})`}
            </Button>
          ))}
        </div>

        {/* Evidence cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="proof-grid">
          {filtered.map(p => {
            const sSt = statusStyles[p.status] || statusStyles['needs-review'];
            const SrcIcon = sourceIcons[p.uploadMethod] || Upload;
            return (
              <Card key={p.id} className={`border ${sSt.border} hover:shadow-md transition-all cursor-pointer group`} onClick={() => setSelected(p)} data-testid={`proof-card-${p.id}`}>
                <CardContent className="py-4 px-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${p.fileType === 'image' ? 'bg-purple-100' : 'bg-slate-100'}`}>
                        {p.fileType === 'image' ? <Image className="w-4 h-4 text-purple-600" /> : <FileText className="w-4 h-4 text-slate-500" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-slate-900 truncate max-w-[180px]">{p.fileName}</p>
                        <p className="text-[10px] text-slate-400">{p.uploadedBy} &middot; {p.uploadDate}</p>
                      </div>
                    </div>
                    <Badge className={`text-[9px] ${sSt.bg} ${sSt.text} border ${sSt.border} shrink-0`}>{sSt.label}</Badge>
                  </div>
                  {p.condition && <p className="text-[10px] text-blue-600 font-medium mb-1">{p.condition}</p>}
                  <p className="text-[10px] text-slate-500 mb-2">{p.project} &middot; {p.stage}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <SrcIcon className="w-3 h-3 text-slate-400" />
                      <span className="text-[9px] text-slate-400">{p.uploadMethod}</span>
                    </div>
                    <ConfidenceBar value={p.matchConfidence} />
                  </div>
                  {p.isBlocker && p.status !== 'approved' && (
                    <div className="flex items-center gap-1 mt-2 text-[10px] text-red-600"><Lock className="w-3 h-3" /> Blocking stage readiness</div>
                  )}
                  {p.aiAssist && (
                    <div className="flex items-center gap-1 mt-1 text-[10px] text-amber-600"><Sparkles className="w-3 h-3" /> AI: {p.aiAssist.recommendation?.slice(0, 50)}...</div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        {filtered.length === 0 && <Card className="border-slate-200"><CardContent className="py-8 text-center text-sm text-slate-400">No evidence items match this filter</CardContent></Card>}

        {/* Detail drawer */}
        <Sheet open={!!selected} onOpenChange={() => setSelected(null)}>
          <SheetContent className="sm:max-w-lg overflow-y-auto" data-testid="proof-detail-drawer">
            {sel && (
              <>
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    {sel.fileType === 'image' ? <Image className="w-5 h-5 text-purple-600" /> : <FileText className="w-5 h-5 text-slate-500" />}
                    {sel.fileName}
                  </SheetTitle>
                  <SheetDescription>Uploaded by {sel.uploadedBy} on {sel.uploadDate}</SheetDescription>
                </SheetHeader>
                <div className="mt-5 space-y-4">
                  {/* Match confidence */}
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-[10px] text-slate-400 font-semibold uppercase mb-2">AI Match Confidence</p>
                    <ConfidenceBar value={sel.matchConfidence} />
                    {sel.condition ? (
                      <p className="text-xs text-blue-600 font-medium mt-2">Matched to: {sel.condition}</p>
                    ) : (
                      <p className="text-xs text-amber-600 mt-2">No condition matched yet. Manual assignment recommended.</p>
                    )}
                  </div>

                  {/* AI Visual Proof Assist */}
                  {sel.aiAssist && (
                    <Card className="border-amber-200 bg-amber-50/50" data-testid="ai-proof-assist">
                      <CardContent className="py-3 px-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-amber-600" />
                          <span className="text-xs font-bold text-amber-800">AI Visual Proof Assist</span>
                        </div>
                        <p className="text-xs text-amber-700 mb-2">Detected: {sel.aiAssist.detected} ({sel.aiAssist.confidence}% confidence)</p>
                        {sel.aiAssist.issues?.length > 0 && (
                          <div className="space-y-1 mb-2">
                            {sel.aiAssist.issues.map((issue, i) => (
                              <div key={i} className="flex items-start gap-1.5 text-[11px] text-amber-700">
                                <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />{issue}
                              </div>
                            ))}
                          </div>
                        )}
                        <p className="text-xs font-medium text-amber-800">{sel.aiAssist.recommendation}</p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Details */}
                  <div className="space-y-2">
                    {[
                      { label: "Project", value: sel.project },
                      { label: "Stage", value: sel.stage },
                      { label: "File size", value: sel.fileSize },
                      { label: "Upload method", value: sel.uploadMethod },
                      { label: "Reviewer", value: sel.reviewer || 'Unassigned' },
                    ].map((r, i) => (
                      <div key={i} className="flex justify-between py-1.5 border-b border-slate-100">
                        <span className="text-xs text-slate-500">{r.label}</span>
                        <span className="text-xs font-medium text-slate-700">{r.value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Cascade preview */}
                  {sel.isBlocker && sel.status === 'needs-review' && sel.conditionId && (
                    <Card className="border-emerald-200 bg-emerald-50/30" data-testid="cascade-preview">
                      <CardContent className="py-3 px-4">
                        <p className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> If you approve this</p>
                        <div className="space-y-1.5">
                          {[
                            `Condition "${sel.condition}" will be cleared`,
                            "Associated blocker will be removed",
                            "Stage readiness will be recalculated",
                            "Trade release status may change to Ready",
                          ].map((effect, i) => (
                            <div key={i} className="flex items-center gap-1.5 text-[11px] text-emerald-700">
                              <CheckCircle2 className="w-3 h-3 shrink-0" />{effect}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Separator />
                  <div className="flex gap-2 flex-wrap">
                    {(sel.status === 'needs-review' || sel.status === 'pending-match') && (
                      <>
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs gap-1.5" onClick={() => handleApprove(sel.id)} data-testid="approve-btn">
                          <ThumbsUp className="w-3.5 h-3.5" /> Approve
                        </Button>
                        <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 text-xs gap-1.5" onClick={() => handleReject(sel.id)} data-testid="reject-btn">
                          <ThumbsDown className="w-3.5 h-3.5" /> Reject
                        </Button>
                      </>
                    )}
                    <Link to="/builder/stage-readiness"><Button variant="outline" size="sm" className="text-xs gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Readiness</Button></Link>
                  </div>
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>
      </div>
    </AppLayout>
  );
}
